import { exportSIMA } from "../exporter";
import { parseSIMA } from "../parser";
import { SIMAElementClassifier, } from "../types";
import { Alignment } from "./alignment";
import { linkProfilesAndCrossSections, rematchOrphanCrossSections, rematchOrphanProfiles, } from "./alignment-linker";
import { CrossSectionGroup } from "./cross-section";
import { Parcel } from "./parcel";
import { LongitudinalProfile } from "./profile";
import { Route } from "./route";
import { SurveyPoint } from "./survey-point";
import { SurveyPointIndex } from "./survey-point-index";
import { TriangulationBlock, } from "./triangulation";
/**
 * SIMA ドキュメントおよびプロジェクト全体の階層構造を表現するモデル。
 */
export class SIMA {
    comment;
    fileAttributeData;
    surveyPoints = [];
    simaRoutes = [];
    parcels = [];
    triangulations = [];
    warnings = [];
    alignments = [];
    // 未結合の独立した縦断・横断成果
    orphanProfiles = [];
    orphanCrossSections = [];
    _pointIndex;
    constructor(data) {
        this.comment = data?.comment;
        this.fileAttributeData = data?.fileAttributeData ?? {
            classifier: SIMAElementClassifier.FILE_ATTRIBUTE,
            version: 2,
            site_name: "",
        };
        if (data?.warnings) {
            this.warnings = [...data.warnings];
        }
        if (data?.surveyPoints) {
            this.surveyPoints = data.surveyPoints.map((p) => p instanceof SurveyPoint ? p : new SurveyPoint(p));
            this._pointIndex = new SurveyPointIndex(this.surveyPoints);
        }
        else {
            this._pointIndex = new SurveyPointIndex();
        }
        if (data?.simaRoutes) {
            this.simaRoutes = data.simaRoutes.map((r) => r instanceof Route ? r : new Route(r, this._pointIndex));
            // 同じ group_number を持つ Route を Alignment に集約
            for (const route of this.simaRoutes) {
                const existing = this.alignments.find((a) => a.groupNumber === route.groupNumber);
                if (existing) {
                    if (!existing.routes.includes(route)) {
                        existing.routes.push(route);
                    }
                }
                else {
                    this.alignments.push(new Alignment([route], this._pointIndex));
                }
            }
        }
        // 縦断成果の初期化
        const profileInstances = (data?.profiles ?? []).map((p) => p instanceof LongitudinalProfile ? p : new LongitudinalProfile(p));
        // 横断成果の初期化
        const crossSectionInstances = (data?.crossSections ?? []).map((cs) => cs instanceof CrossSectionGroup ? cs : new CrossSectionGroup(cs));
        // 画地の初期化
        if (data?.parcels) {
            this.parcels = data.parcels.map((p) => p instanceof Parcel ? p : new Parcel(p, this._pointIndex));
        }
        // 求積ブロックの初期化
        if (data?.triangulations) {
            this.triangulations = data.triangulations.map((t) => t instanceof TriangulationBlock
                ? t
                : new TriangulationBlock(t, this._pointIndex));
        }
        // 縦断成果・横断成果と線形とのリレーショナル結合を実行
        const linkingResult = linkProfilesAndCrossSections(this.alignments, profileInstances, crossSectionInstances);
        this.orphanProfiles = linkingResult.orphanProfiles;
        this.orphanCrossSections = linkingResult.orphanCrossSections;
        this.warnings.push(...linkingResult.warnings);
    }
    /**
     * group_number ごとにグループ化された路線 Map を取得します。
     */
    get groups() {
        const groupMap = new Map();
        for (const route of this.simaRoutes) {
            const groupNo = route.groupNumber;
            const list = groupMap.get(groupNo) ?? [];
            list.push(route);
            groupMap.set(groupNo, list);
        }
        return groupMap;
    }
    /**
     * 点名・点番号・複合キーから SurveyPoint を参照可能な共有座標プール Map を返却します。
     */
    get sharedCoordinatePool() {
        return this._pointIndex.toPoolMap();
    }
    /**
     * 結合済みおよび孤立を含むすべての縦断成果を取得します。
     */
    get profiles() {
        const list = [];
        for (const align of this.alignments) {
            if (align.longitudinalProfile) {
                list.push(align.longitudinalProfile);
            }
        }
        return list.concat(this.orphanProfiles);
    }
    /**
     * 結合済みおよび孤立を含むすべての横断成果グループを取得します。
     */
    get crossSections() {
        const list = [];
        for (const align of this.alignments) {
            if (align.crossSections) {
                list.push(align.crossSections);
            }
        }
        return list.concat(this.orphanCrossSections);
    }
    /**
     * SIMA テキスト文字列を解析してクラス階層構造インスタンスを生成します。
     */
    static parse(content) {
        const parsedFormat = parseSIMA(content);
        return SIMA.fromFormat(parsedFormat);
    }
    /**
     * ISIMAFormat オブジェクトから SIMA クラスインスタンスを生成します。
     */
    static fromFormat(format) {
        return new SIMA(format);
    }
    /**
     * SIMA テキストフォーマットへシリアライズします。
     */
    export(options) {
        return exportSIMA(this.toFormat(), options);
    }
    /**
     * 測量座標点をドキュメントに追加し、インデックスと関連モデルの参照を更新します。
     */
    addSurveyPoint(point) {
        return this.addSurveyPoints([point])[0];
    }
    /**
     * 複数の測量座標点を一括追加し、インデックス更新後に1度だけ関連モデルの座標を解決します。
     */
    addSurveyPoints(points) {
        if (points.length === 0)
            return [];
        const instances = [];
        for (const point of points) {
            const instance = point instanceof SurveyPoint ? point : new SurveyPoint(point);
            this.surveyPoints.push(instance);
            this._pointIndex.add(instance);
            instances.push(instance);
        }
        for (const route of this.simaRoutes) {
            route.resolveSurveyPoints(this._pointIndex);
        }
        for (const parcel of this.parcels) {
            parcel.resolvePoints(this._pointIndex);
        }
        for (const tri of this.triangulations) {
            tri.resolvePoints(this._pointIndex);
        }
        return instances;
    }
    /**
     * 点番号または点名から測量座標点を O(1) で検索します。
     */
    findSurveyPoint(pointNumberOrName) {
        if (typeof pointNumberOrName === "number") {
            return this._pointIndex.lookup(pointNumberOrName);
        }
        return this._pointIndex.lookup(undefined, pointNumberOrName);
    }
    /**
     * 路線番号またはグループ番号から線形を検索します。
     */
    findAlignment(routeNumberOrGroupNumber) {
        return this.alignments.find((a) => a.groupNumber === routeNumberOrGroupNumber ||
            a.hasRouteNumber(routeNumberOrGroupNumber));
    }
    /**
     * 線形を追加し、座標解決と孤立成果の再照合を行います。
     */
    addAlignment(alignment) {
        let instance;
        if (alignment instanceof Alignment) {
            instance = alignment;
            for (const route of instance.routes) {
                if (!this.simaRoutes.includes(route)) {
                    this.simaRoutes.push(route);
                }
            }
            const existing = this.alignments.find((a) => a.groupNumber === instance.groupNumber);
            if (existing && existing !== instance) {
                for (const r of instance.routes) {
                    if (!existing.routes.includes(r)) {
                        existing.routes.push(r);
                    }
                }
                instance = existing;
            }
            else if (!existing) {
                this.alignments.push(instance);
            }
        }
        else {
            const routeInstance = alignment instanceof Route
                ? alignment
                : new Route(alignment, this._pointIndex);
            if (!this.simaRoutes.includes(routeInstance)) {
                this.simaRoutes.push(routeInstance);
            }
            let existing = this.alignments.find((a) => a.groupNumber === routeInstance.groupNumber);
            if (!existing) {
                existing = new Alignment([routeInstance], this._pointIndex);
                this.alignments.push(existing);
            }
            else if (!existing.routes.includes(routeInstance)) {
                existing.routes.push(routeInstance);
            }
            instance = existing;
        }
        instance.resolveSurveyPoints(this._pointIndex);
        rematchOrphanProfiles(instance, this.orphanProfiles, this.warnings);
        rematchOrphanCrossSections(instance, this.orphanCrossSections, this.warnings);
        return instance;
    }
    /**
     * 路線を追加し、該当グループ番号の Alignment に集約します。
     */
    addRoute(route) {
        const routeInstance = route instanceof Route ? route : new Route(route, this._pointIndex);
        if (!this.simaRoutes.includes(routeInstance)) {
            this.simaRoutes.push(routeInstance);
        }
        let align = this.alignments.find((a) => a.groupNumber === routeInstance.groupNumber);
        if (!align) {
            align = new Alignment([routeInstance], this._pointIndex);
            this.alignments.push(align);
        }
        else if (!align.routes.includes(routeInstance)) {
            align.routes.push(routeInstance);
        }
        rematchOrphanProfiles(align, this.orphanProfiles, this.warnings);
        rematchOrphanCrossSections(align, this.orphanCrossSections, this.warnings);
        return routeInstance;
    }
    /**
     * 縦断成果を追加し、一致する路線番号を持つ線形へ関係付けます。
     */
    addProfile(profile) {
        const instance = profile instanceof LongitudinalProfile
            ? profile
            : new LongitudinalProfile(profile);
        instance.sortStations();
        const matchedAlignment = this.findAlignment(instance.routeNumber);
        if (matchedAlignment) {
            matchedAlignment.linkProfile(instance);
        }
        else {
            this.orphanProfiles.push(instance);
        }
        return instance;
    }
    /**
     * 横断成果グループを追加し、一致する路線番号を持つ線形へ関係付けます。
     */
    addCrossSectionGroup(crossSectionGroup) {
        const instance = crossSectionGroup instanceof CrossSectionGroup
            ? crossSectionGroup
            : new CrossSectionGroup(crossSectionGroup);
        instance.sortStations();
        const matchedAlignment = this.findAlignment(instance.routeNumber);
        if (matchedAlignment) {
            matchedAlignment.linkCrossSections(instance);
        }
        else {
            this.orphanCrossSections.push(instance);
        }
        return instance;
    }
    /**
     * 画地を追加します。
     */
    addParcel(parcel) {
        const instance = parcel instanceof Parcel ? parcel : new Parcel(parcel, this._pointIndex);
        this.parcels.push(instance);
        return instance;
    }
    /**
     * 求積ブロックを追加します。
     */
    addTriangulation(triangulation) {
        const instance = triangulation instanceof TriangulationBlock
            ? triangulation
            : new TriangulationBlock(triangulation, this._pointIndex);
        this.triangulations.push(instance);
        return instance;
    }
    /**
     * ISIMAFormat データ構造に変換します。
     */
    toFormat() {
        const profilesData = this.profiles.map((p) => p.toProfileData());
        const crossSectionsData = this.crossSections.map((cs) => cs.toGroupData());
        const parcelsData = this.parcels.map((p) => p.toParcelData());
        const triangulationsData = this.triangulations.map((t) => t.toTriangulationData());
        return {
            comment: this.comment,
            fileAttributeData: this.fileAttributeData,
            surveyPoints: this.surveyPoints.map((sp) => sp.toCoord()),
            simaRoutes: this.simaRoutes.map((r) => r.toRouteData()),
            profiles: profilesData.length > 0 ? profilesData : undefined,
            crossSections: crossSectionsData.length > 0 ? crossSectionsData : undefined,
            parcels: parcelsData.length > 0 ? parcelsData : undefined,
            triangulations: triangulationsData.length > 0 ? triangulationsData : undefined,
            warnings: this.warnings.length > 0 ? this.warnings : undefined,
        };
    }
}
//# sourceMappingURL=sima.js.map